import React, { useState, useEffect, useRef, createRef } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { navLinks } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection, usePrefersReducedMotion, useLanguage } from '@hooks';
import { Menu, LanguageButton } from '@components';

import logoImage from '@images/logo.png';

const StyledHeader = styled.header`
  ${({ theme }) => theme.mixins.flexBetween};
  position: fixed;
  top: 0;
  z-index: 11;
  padding: 0px 50px;
  width: 100%;
  height: var(--nav-height);
  background: ${props => (props.scrolledToTop ? 'transparent' : 'rgba(0, 0, 0, 0.85)')};
  backdrop-filter: ${props => (props.scrolledToTop ? 'none' : 'blur(20px) saturate(180%)')};
  border-bottom: ${props => (props.scrolledToTop ? 'none' : '1px solid rgba(255, 255, 255, 0.1)')};
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  @media (max-width: 1080px) {
    padding: 0 40px;
  }
  @media (max-width: 768px) {
    padding: 0 25px;
  }

  @media (prefers-reduced-motion: no-preference) {
    ${props =>
    props.scrollDirection === 'up' &&
      !props.scrolledToTop &&
      css`
        height: var(--nav-scroll-height);
        transform: translateY(0px);
        background: rgba(0, 0, 0, 0.9);
        backdrop-filter: blur(20px) saturate(180%);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      `};

    ${props =>
    props.scrollDirection === 'down' &&
      !props.scrolledToTop &&
      css`
        height: var(--nav-scroll-height);
        transform: translateY(calc(var(--nav-scroll-height) * -1));
        background: rgba(0, 0, 0, 0.9);
        backdrop-filter: blur(20px) saturate(180%);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      `};
  }
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.flexBetween};
  position: relative;
  width: 100%;
  color: var(--text-primary); // 统一使用白色
  font-family: var(--font-mono);
  counter-reset: item 0;
  z-index: 12;

  .logo {
    ${({ theme }) => theme.mixins.flexCenter};

    a {
      color: var(--text-accent); // 深绿色
      width: 42px;
      height: 42px;
      position: relative;
      z-index: 1;
      display: block;

      .logo-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 2;
        @media (prefers-reduced-motion: no-preference) {
          transition: var(--transition);
        }
      }

      .logo-background {
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, var(--text-accent) 0%, rgba(100, 255, 218, 0.1) 100%);
        border-radius: 6px;
        opacity: 0;
        z-index: 1;
        @media (prefers-reduced-motion: no-preference) {
          transition: opacity 0.3s ease, transform 0.3s ease;
        }
      }

      &:hover,
      &:focus {
        outline: 0;

        .logo-background {
          opacity: 1;
          transform: scale(1.05);
        }

        .logo-image {
          transform: scale(1.1);
        }
      }
    }
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 15px;

  @media (max-width: 768px) {
    display: none;
  }

  ol {
    ${({ theme }) => theme.mixins.flexBetween};
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      margin: 0 5px;
      position: relative;
      counter-increment: item 1;
      font-size: var(--fz-xs);

      a {
        padding: 10px;
        color: var(--text-primary); // 统一使用白色
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        font-weight: 500;

        &:before {
          content: '0' counter(item) '.';
          margin-right: 5px;
          color: var(--text-accent); // 深绿色
          font-size: var(--fz-xxs);
          text-align: right;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
        }

        &:hover,
        &:focus {
          color: var(--text-accent); // 深绿色
          text-shadow: 0 0 8px rgba(100, 255, 218, 0.6);
        }
      }
    }
  }

  .resume-button {
    ${({ theme }) => theme.mixins.smallButton};
    color: var(--text-accent) !important; // 深绿色
    background-color: transparent;
    border: 1px solid var(--text-accent); // 深绿色边框
    border-radius: var(--border-radius);
    padding: 0.75rem 1rem;
    font-size: var(--fz-xs);
    font-family: var(--font-mono);
    line-height: 1;
    text-decoration: none;
    cursor: pointer;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      outline: none;
      box-shadow: 3px 3px 0 0 var(--text-accent); // 与语言按钮一致的阴影效果
      transform: translate(-4px, -4px); // 与语言按钮一致的位移效果
    }

    &:after {
      display: none !important;
    }
  }
`;

const Nav = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome);
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { t } = useLanguage();

  // 创建 refs 用于 CSSTransition
  const logoRef = useRef(null);
  const menuRef = useRef(null);
  const resumeRef = useRef(null);
  const languageRef = useRef(null);
  const navItemRefs = useRef(navLinks.map(() => createRef()));

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50);
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeout = isHome ? loaderDelay : 0;
  const fadeClass = isHome ? 'fade' : '';
  const fadeDownClass = isHome ? 'fadedown' : '';

  const Logo = (
    <div className="logo" tabIndex="-1">
      {isHome ? (
        <a href="/" aria-label="home">
          <div className="logo-background"></div>
          <img src={logoImage} alt="Logo" className="logo-image" />
        </a>
      ) : (
        <Link to="/" aria-label="home">
          <div className="logo-background"></div>
          <img src={logoImage} alt="Logo" className="logo-image" />
        </Link>
      )}
    </div>
  );

  const ResumeLink = (
    <Link className="resume-button" to="/resume">
      {t('resume')}
    </Link>
  );

  return (
    <StyledHeader scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <StyledNav>
        {prefersReducedMotion ? (
          <>
            {Logo}

            <StyledLinks>
              <ol>
                {navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <li key={i}>
                      <Link to={url}>{t(name.toLowerCase())}</Link>
                    </li>
                  ))}
              </ol>
              <div>{ResumeLink}</div>
              <LanguageButton />
            </StyledLinks>

            <Menu />
          </>
        ) : (
          <>
            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout} nodeRef={logoRef}>
                  <div ref={logoRef}>{Logo}</div>
                </CSSTransition>
              )}
            </TransitionGroup>

            <StyledLinks>
              <ol>
                <TransitionGroup component={null}>
                  {isMounted &&
                    navLinks &&
                    navLinks.map(({ url, name }, i) => (
                      <CSSTransition
                        key={i}
                        classNames={fadeDownClass}
                        timeout={timeout}
                        nodeRef={navItemRefs.current[i]}>
                        <li
                          ref={navItemRefs.current[i]}
                          key={i}
                          style={{ transitionDelay: `${isMounted && isHome ? i * 100 : 0}ms` }}>
                          <Link to={url}>{t(name.toLowerCase())}</Link>
                        </li>
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </ol>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout} nodeRef={resumeRef}>
                    <div
                      ref={resumeRef}
                      style={{
                        transitionDelay: `${isMounted && isHome ? navLinks.length * 100 : 0}ms`,
                      }}>
                      {ResumeLink}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout} nodeRef={languageRef}>
                    <div
                      ref={languageRef}
                      style={{
                        transitionDelay: `${
                          isMounted && isHome ? (navLinks.length + 1) * 100 : 0
                        }ms`,
                      }}>
                      <LanguageButton />
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </StyledLinks>

            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout} nodeRef={menuRef}>
                  <div ref={menuRef}>
                    <Menu />
                  </div>
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        )}
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
};

export default Nav;
